/**
 * SDL Dice Screen — manage owned dice, equip loadout presets, browse store
 *
 * Interaction: tap-and-hold a dice thumbnail, drag it onto a loadout slot to equip.
 * Tap a filled slot to unequip. Switch presets via the numbered row.
 */

import { EventEmitter } from '../../engine/core/EventEmitter';

// ─── Types ───────────────────────────────────────────────────────────
export interface DiceItem {
  id: string;
  thumbnailUrl: string;
}

export interface DicePreset {
  slots: (string | null)[]; // 3 entries: diceId or null
}

interface DiceScreenEvents {
  'action': { action: string };
  'equip': { preset: number; slot: number; diceId: string | null };
  'preset:switch': { preset: number };
}

// ─── Constants ───────────────────────────────────────────────────────
const NUM_PRESETS = 12;
const NUM_SLOTS = 3;
const HOLD_DELAY = 300; // ms before drag starts

// ─── Component ───────────────────────────────────────────────────────
export class DiceScreen extends EventEmitter<DiceScreenEvents> {
  public element: HTMLElement;

  // State
  private ownedDice: DiceItem[] = [];
  private presets: DicePreset[] = [];
  private activePreset = 0;

  // Drag state
  private isDragging = false;
  private dragSourceEl: HTMLElement | null = null;
  private dragSourceDiceId: string | null = null;
  private holdTimer: ReturnType<typeof setTimeout> | null = null;
  private ghostEl!: HTMLElement;
  /**
   * Timestamp of the most recent drop. The slot's `click` listener
   * checks against this to suppress the synthetic click that browsers
   * fire on `touchend`. Without the gate, a successful drop is
   * immediately followed by a "tap-to-unequip" → slot empties on every
   * drop. 350ms covers Android's slowest synthetic-click delay.
   */
  private dragEndedAt = 0;

  // DOM refs
  private slotsContainer!: HTMLElement;
  private gridContainer!: HTMLElement;
  private instructionEl!: HTMLElement;
  private presetRow!: HTMLElement;

  constructor() {
    super();
    this.initPresets();
    this.element = this.createElement();
  }

  // ─── Public API ──────────────────────────────────────────────────

  /** Set owned dice list and re-render grid */
  setOwnedDice(dice: DiceItem[]): void {
    this.ownedDice = dice;
    this.renderGrid();
    this.refreshEquippedBadges();
  }

  /** Set presets from server and re-render */
  setPresets(presets: DicePreset[]): void {
    for (let i = 0; i < NUM_PRESETS; i++) {
      this.presets[i] = presets[i] || { slots: [null, null, null] };
    }
    this.renderSlots();
    this.refreshEquippedBadges();
    this.updatePresetDots();
  }

  /** Set active preset index (server-driven, no event emitted) */
  setActivePreset(index: number): void {
    this.activePreset = index;
    this.presetRow.querySelectorAll('.dice-preset-dot').forEach((dot, i) => {
      dot.classList.toggle('active', i === index);
    });
    this.renderSlots();
    this.refreshEquippedBadges();
  }

  /** Get the currently active preset index */
  getActivePreset(): number {
    return this.activePreset;
  }

  /** Get the slot array for a given preset */
  getPresetSlots(preset: number): (string | null)[] {
    return [...(this.presets[preset]?.slots ?? [null, null, null])];
  }

  destroy(): void {
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);
    this.element.remove();
    this.removeAllListeners();
  }

  // ─── Init ────────────────────────────────────────────────────────

  private initPresets(): void {
    for (let i = 0; i < NUM_PRESETS; i++) {
      this.presets.push({ slots: [null, null, null] });
    }
  }

  private get currentSlots(): (string | null)[] {
    return this.presets[this.activePreset].slots;
  }

  // ─── DOM Creation ────────────────────────────────────────────────

  private createElement(): HTMLElement {
    const root = document.createElement('div');

    root.innerHTML = `
      <div class="dice-screen-content">
        <!-- Screen header -->
        <div class="sdl-screen-header">
          <div class="sdl-screen-title cyan">DICE</div>
          <div class="sdl-screen-subtitle">YOUR COLLECTION</div>
        </div>

        <!-- Tab strip -->
        <div class="dice-tab-strip">
          <button class="dice-tab-btn active" data-tab="my-dice">MY DICE</button>
          <button class="dice-tab-btn" data-tab="dice-store">DICE STORE</button>
        </div>

        <!-- MY DICE panel -->
        <div class="dice-tab-panel active" data-panel="my-dice">
          <!-- Preset row -->
          <div class="dice-preset-row">
            ${Array.from({ length: NUM_PRESETS }, (_, i) =>
              `<div class="dice-preset-dot${i === 0 ? ' active' : ''}" data-preset="${i}">${i + 1}</div>`
            ).join('')}
          </div>

          <!-- Loadout slots -->
          <div class="dice-loadout">
            <div class="dice-loadout-labels">
              <span class="dice-mode-label dice-mode-craps">STREET CRAPS</span>
              <span class="dice-mode-label dice-mode-craps">STREET CRAPS</span>
              <span class="dice-mode-label dice-mode-ceelo">CEE-LO</span>
            </div>
            <div class="dice-loadout-slots">
              ${Array.from({ length: NUM_SLOTS }, (_, i) => `
                <div class="dice-slot" data-slot="${i}">
                  <div class="dice-slot-number">${i + 1}</div>
                  <div class="dice-slot-thumb dice-slot-empty">
                    <span class="dice-slot-plus">+</span>
                  </div>
                  <img class="dice-slot-base" src="/assets/ui/dice-base.png" draggable="false">
                </div>
              `).join('')}
            </div>
          </div>

          <!-- Instruction text -->
          <div class="dice-instruction">TAP AND HOLD DICE TO DRAG TO SLOTS</div>

          <!-- Thumbnail grid -->
          <div class="dice-grid"></div>

          <!-- TRY button -->
          <button class="dice-try-btn" data-sound="button">TRY</button>
        </div>

        <!-- DICE STORE panel -->
        <div class="dice-tab-panel" data-panel="dice-store">
          <div class="sdl-empty-state">
            <div class="sdl-empty-icon">&#x1F3EA;</div>
            <div class="sdl-empty-title">COMING SOON</div>
            <div class="sdl-empty-text">Weekly rotation drops every Monday.<br>Check back for exclusive skins.</div>
          </div>
        </div>
      </div>

      <!-- Drag ghost -->
      <div class="dice-drag-ghost">
        <img class="dice-drag-ghost-img" src="" draggable="false">
      </div>
    `;

    this.injectStyles();

    // Cache refs
    this.slotsContainer = root.querySelector('.dice-loadout-slots')!;
    this.gridContainer = root.querySelector('.dice-grid')!;
    this.instructionEl = root.querySelector('.dice-instruction')!;
    this.presetRow = root.querySelector('.dice-preset-row')!;
    this.ghostEl = root.querySelector('.dice-drag-ghost')!;

    this.setupTabListeners(root);
    this.setupPresetListeners();
    this.setupSlotListeners();
    this.setupGlobalDragListeners();

    return root;
  }

  // ─── Tab Switching ───────────────────────────────────────────────

  private setupTabListeners(root: HTMLElement): void {
    const tabs = root.querySelectorAll<HTMLElement>('.dice-tab-btn');
    const panels = root.querySelectorAll<HTMLElement>('.dice-tab-panel');

    tabs.forEach(btn => {
      btn.addEventListener('click', () => {
        const target = btn.dataset.tab;
        tabs.forEach(b => b.classList.toggle('active', b === btn));
        panels.forEach(p => p.classList.toggle('active', p.dataset.panel === target));
      });
    });
  }

  // ─── Preset Switching ────────────────────────────────────────────

  private setupPresetListeners(): void {
    this.presetRow.addEventListener('click', (e) => {
      const dot = (e.target as HTMLElement).closest<HTMLElement>('.dice-preset-dot');
      if (!dot) return;
      const index = parseInt(dot.dataset.preset!, 10);
      this.switchPreset(index);
    });
  }

  private switchPreset(index: number): void {
    this.activePreset = index;

    // Update dots
    this.presetRow.querySelectorAll('.dice-preset-dot').forEach((dot, i) => {
      dot.classList.toggle('active', i === index);
    });

    this.renderSlots();
    this.refreshEquippedBadges();
    this.emit('preset:switch', { preset: index });
  }

  private updatePresetDots(): void {
    this.presetRow.querySelectorAll('.dice-preset-dot').forEach((dot, i) => {
      const hasAny = this.presets[i].slots.some(s => s !== null);
      dot.classList.toggle('has-dice', hasAny);
    });
  }

  // ─── Slot Rendering ──────────────────────────────────────────────

  private renderSlots(): void {
    const slotEls = this.slotsContainer.querySelectorAll<HTMLElement>('.dice-slot');
    slotEls.forEach((slotEl, i) => {
      const diceId = this.currentSlots[i];
      const thumbEl = slotEl.querySelector<HTMLElement>('.dice-slot-thumb')!;

      if (diceId) {
        const dice = this.ownedDice.find(d => d.id === diceId);
        const fallback = '/assets/ui/dice-thumb-placeholder.png';
        const url = dice?.thumbnailUrl || fallback;
        slotEl.classList.add('filled');
        thumbEl.className = 'dice-slot-thumb';
        thumbEl.innerHTML = `<img src="${url}" onerror="this.onerror=null;this.src='${fallback}'" draggable="false">`;
      } else {
        slotEl.classList.remove('filled');
        thumbEl.className = 'dice-slot-thumb dice-slot-empty';
        thumbEl.innerHTML = '<span class="dice-slot-plus">+</span>';
      }
    });
    this.updatePresetDots();
  }

  private setupSlotListeners(): void {
    this.slotsContainer.addEventListener('click', (e) => {
      // Suppress the synthetic click that fires on `touchend` right
      // after a drop. Without this guard, dragging a die onto a slot
      // equips it (drop) and then immediately unequips it (synthetic
      // tap) — slot ends up empty.
      if (performance.now() - this.dragEndedAt < 350) return;
      const slotEl = (e.target as HTMLElement).closest<HTMLElement>('.dice-slot');
      if (!slotEl) return;
      const index = parseInt(slotEl.dataset.slot!, 10);
      if (this.currentSlots[index]) {
        this.currentSlots[index] = null;
        this.renderSlots();
        this.refreshEquippedBadges();
        this.emit('equip', { preset: this.activePreset, slot: index, diceId: null });
      }
    });
  }

  // ─── Grid Rendering ──────────────────────────────────────────────

  private renderGrid(): void {
    const fallback = '/assets/ui/dice-thumb-placeholder.png';
    this.gridContainer.innerHTML = this.ownedDice.map(dice => `
      <div class="dice-thumb" data-dice="${dice.id}">
        <div class="dice-thumb-img">
          <img src="${dice.thumbnailUrl}" onerror="this.onerror=null;this.src='${fallback}'" draggable="false">
        </div>
      </div>
    `).join('');

    this.setupGridDragListeners();
    this.refreshEquippedBadges();
  }

  private refreshEquippedBadges(): void {
    // Clear all badges
    this.gridContainer.querySelectorAll<HTMLElement>('.dice-thumb').forEach(thumb => {
      thumb.classList.remove('equipped');
      const badge = thumb.querySelector('.dice-equipped-badge');
      if (badge) badge.remove();
    });

    // Set badges for current preset
    this.currentSlots.forEach((diceId, slotIndex) => {
      if (!diceId) return;
      const thumb = this.gridContainer.querySelector<HTMLElement>(`.dice-thumb[data-dice="${diceId}"]`);
      if (!thumb) return;
      thumb.classList.add('equipped');
      const badge = document.createElement('div');
      badge.className = 'dice-equipped-badge';
      badge.textContent = String(slotIndex + 1);
      thumb.prepend(badge);
    });
  }

  // ─── Drag & Drop ─────────────────────────────────────────────────

  private setupGridDragListeners(): void {
    this.gridContainer.querySelectorAll<HTMLElement>('.dice-thumb').forEach(thumb => {
      // Touch
      thumb.addEventListener('touchstart', (e) => {
        const t = e.touches[0];
        this.holdTimer = setTimeout(() => {
          this.startDrag(thumb, t.clientX, t.clientY);
        }, HOLD_DELAY);
      }, { passive: true });

      thumb.addEventListener('touchmove', (e) => {
        if (this.isDragging) {
          e.preventDefault();
          const t = e.touches[0];
          this.moveDrag(t.clientX, t.clientY);
        } else {
          this.cancelHold();
        }
      }, { passive: false });

      thumb.addEventListener('touchend', (e) => {
        this.cancelHold();
        if (this.isDragging) {
          const t = e.changedTouches[0];
          this.endDrag(t.clientX, t.clientY);
        }
      });

      thumb.addEventListener('touchcancel', () => {
        this.cancelHold();
        if (this.isDragging) this.endDrag(-1, -1);
      });

      // Mouse
      thumb.addEventListener('mousedown', (e) => {
        e.preventDefault();
        this.holdTimer = setTimeout(() => {
          this.startDrag(thumb, e.clientX, e.clientY);
        }, HOLD_DELAY);
      });
    });
  }

  private setupGlobalDragListeners(): void {
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
  }

  private onMouseMove(e: MouseEvent): void {
    if (this.isDragging) this.moveDrag(e.clientX, e.clientY);
  }

  private onMouseUp(e: MouseEvent): void {
    this.cancelHold();
    if (this.isDragging) this.endDrag(e.clientX, e.clientY);
  }

  private cancelHold(): void {
    if (this.holdTimer) {
      clearTimeout(this.holdTimer);
      this.holdTimer = null;
    }
  }

  private startDrag(thumbEl: HTMLElement, x: number, y: number): void {
    this.isDragging = true;
    this.dragSourceEl = thumbEl;
    this.dragSourceDiceId = thumbEl.dataset.dice || null;
    thumbEl.classList.add('dragging-source');
    // Mark a global drag so UIManager's screen-swipe detector skips
    // the horizontal X-threshold check while a drop is in progress.
    // Without this, dropping into the center slot (after a diagonal
    // drag from the grid) reads as a swipe → navigates away.
    document.body.dataset.diceDragging = 'true';

    // Set ghost image
    const img = thumbEl.querySelector<HTMLImageElement>('.dice-thumb-img img');
    const ghostImg = this.ghostEl.querySelector<HTMLImageElement>('.dice-drag-ghost-img')!;
    ghostImg.src = img?.src || '';
    this.ghostEl.style.left = x + 'px';
    this.ghostEl.style.top = y + 'px';
    this.ghostEl.classList.add('visible');

    this.instructionEl.textContent = 'DROP DICE ON A SLOT';
    this.instructionEl.classList.add('dragging');
  }

  private moveDrag(x: number, y: number): void {
    this.ghostEl.style.left = x + 'px';
    this.ghostEl.style.top = y + 'px';

    // Highlight hovered slot
    this.slotsContainer.querySelectorAll<HTMLElement>('.dice-slot').forEach(slot => {
      const rect = slot.getBoundingClientRect();
      const over = x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom;
      slot.classList.toggle('drop-hover', over);
    });
  }

  private endDrag(x: number, y: number): void {
    if (!this.isDragging) return;

    // Find drop target slot
    let targetSlot = -1;
    this.slotsContainer.querySelectorAll<HTMLElement>('.dice-slot').forEach((slot, i) => {
      slot.classList.remove('drop-hover');
      const rect = slot.getBoundingClientRect();
      if (x >= rect.left && x <= rect.right && y >= rect.top && y <= rect.bottom) {
        targetSlot = i;
      }
    });

    if (targetSlot >= 0 && this.dragSourceDiceId) {
      // Place dice in target slot, overwriting whatever was there.
      // Deliberately NOT clearing other slots that contain the same
      // dice ID — players are allowed to equip the same skin across
      // multiple slots (e.g. matched-set loadouts). The previous
      // indexOf-based removal cleared the FIRST other slot containing
      // the dragged dice, which emptied a different slot on what the
      // player perceived as a "fill" operation. To clear a slot,
      // tap-to-unequip is the dedicated affordance.
      this.currentSlots[targetSlot] = this.dragSourceDiceId;
      this.renderSlots();
      this.refreshEquippedBadges();
      this.emit('equip', { preset: this.activePreset, slot: targetSlot, diceId: this.dragSourceDiceId });
    }

    // Cleanup
    if (this.dragSourceEl) this.dragSourceEl.classList.remove('dragging-source');
    this.isDragging = false;
    this.dragSourceEl = null;
    this.dragSourceDiceId = null;
    this.ghostEl.classList.remove('visible');
    this.instructionEl.textContent = 'TAP AND HOLD DICE TO DRAG TO SLOTS';
    this.instructionEl.classList.remove('dragging');
    this.dragEndedAt = performance.now();
    // DON'T clear the body marker synchronously. The thumb's touchend
    // / mouseup handler runs first (target), then the event bubbles to
    // `screenContainer.touchend` which reads this marker to decide
    // whether to run the swipe-navigate handler. Clearing here would
    // make the swipe detector see no drag-in-progress and navigate
    // away on a diagonal drop. Defer to the next macrotask so the
    // bubble path sees the marker still set.
    setTimeout(() => {
      delete document.body.dataset.diceDragging;
    }, 50);
  }

  // ─── Styles ──────────────────────────────────────────────────────

  private injectStyles(): void {
    if (document.getElementById('dice-screen-styles')) return;
    const styles = document.createElement('style');
    styles.id = 'dice-screen-styles';
    styles.textContent = `
      /* ── Layout ── */
      .dice-screen-content {
        display: flex;
        flex-direction: column;
        height: 100%;
      }
      .dice-screen-content .sdl-screen-header {
        padding-bottom: 0;
        flex-shrink: 0;
      }

      /* ── Tab Strip ── */
      .dice-tab-strip {
        display: flex;
        border-bottom: 1px solid var(--glass-border);
        flex-shrink: 0;
      }
      .dice-tab-btn {
        flex: 1;
        padding: 14px 0;
        background: transparent;
        border: none;
        color: rgba(255,255,255,0.35);
        font-family: var(--font-display);
        font-size: 16px;
        letter-spacing: 2px;
        cursor: pointer;
        position: relative;
        transition: color 0.2s;
        -webkit-tap-highlight-color: transparent;
      }
      .dice-tab-btn.active { color: var(--cyan); }
      .dice-tab-btn.active::after {
        content: '';
        position: absolute;
        bottom: 0; left: 16px; right: 16px;
        height: 2px;
        background: var(--cyan);
        border-radius: 2px 2px 0 0;
        box-shadow: 0 0 8px rgba(0,245,255,0.5);
      }
      .dice-tab-btn:active { opacity: 0.7; }

      /* ── Tab Panels ── */
      .dice-tab-panel {
        display: none;
        flex: 1;
        overflow-y: auto;
        overflow-x: hidden;
        -webkit-overflow-scrolling: touch;
        position: relative;
      }
      .dice-tab-panel.active {
        display: flex;
        flex-direction: column;
      }

      /* ── Preset Row ── */
      /* Grid keeps all 12 dots on one row regardless of viewport width.
         On narrow phones (≤414px) the dots shrink to fit; on wider
         screens they cap at 28px and the row centers. */
      .dice-preset-row {
        display: grid;
        grid-template-columns: repeat(12, minmax(0, 1fr));
        gap: 6px;
        padding: 12px 16px 0;
        flex-shrink: 0;
        max-width: 420px;
        margin: 0 auto;
        width: 100%;
        box-sizing: border-box;
      }
      .dice-preset-dot {
        width: 100%;
        aspect-ratio: 1 / 1;
        max-width: 28px;
        max-height: 28px;
        justify-self: center;
        border-radius: 8px;
        background: var(--concrete-light);
        border: 1.5px solid var(--glass-border);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 11px;
        font-weight: 700;
        color: rgba(255,255,255,0.25);
        cursor: pointer;
        transition: all 0.2s;
        -webkit-tap-highlight-color: transparent;
      }
      .dice-preset-dot:active { transform: scale(0.9); }
      .dice-preset-dot.active {
        background: var(--cyan);
        border-color: var(--cyan);
        color: var(--dark-bg);
        box-shadow: 0 0 12px rgba(0,245,255,0.35);
      }
      .dice-preset-dot.has-dice {
        border-color: rgba(255,255,255,0.12);
        color: rgba(255,255,255,0.4);
      }
      .dice-preset-dot.has-dice.active {
        background: var(--cyan);
        border-color: var(--cyan);
        color: var(--dark-bg);
      }

      /* ── Loadout ── */
      .dice-loadout {
        flex-shrink: 0;
        padding: 10px 16px 0;
      }
      .dice-loadout-labels {
        display: flex;
        gap: 8px;
        margin-bottom: 6px;
      }
      .dice-mode-label {
        flex: 1;
        text-align: center;
        font-size: 10px;
        font-weight: 700;
        letter-spacing: 1px;
        text-transform: uppercase;
      }
      .dice-mode-craps { color: var(--green-dim); }
      .dice-mode-ceelo { color: var(--cyan-dim); }

      .dice-loadout-slots {
        display: flex;
        gap: 8px;
      }

      .dice-slot {
        flex: 1;
        position: relative;
        height: 6rem;
        display: flex;
        flex-direction: column;
        justify-content: flex-end;
        align-items: center;
        background: transparent;
        border: none;
        transition: all 0.2s;
        overflow: visible;
      }
      .dice-slot.filled { }
      .dice-slot.drop-hover {
        transform: scale(1.1);
      }

      .dice-slot-number {
        display: none;
      }

      .dice-slot-thumb {
        position: absolute;
        bottom: 0.8rem;
        left: 50%;
        transform: translateX(-50%);
        width: 5rem;
        height: auto;
        z-index: 1;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .dice-slot-thumb img {
        width: 100%;
        height: auto;
        object-fit: contain;
        filter: drop-shadow(2px 2px 2px black);
      }
      .dice-slot-empty {
        position: absolute;
        bottom: 1.5rem;
        left: 50%;
        transform: translateX(-50%);
        width: 3rem;
        height: 3rem;
        border: 2px dashed rgba(255,255,255,0.35);
        border-radius: 10px;
        background: rgba(255,255,255,0.03);
        box-shadow: 0 0 8px rgba(255,255,255,0.05);
      }
      .dice-slot-plus {
        font-size: 24px;
        color: rgba(255,255,255,0.4);
        font-weight: 300;
        line-height: 1;
      }

      .dice-slot-base {
        position: absolute;
        bottom: 0;
        left: 50%;
        transform: translateX(-50%);
        width: 6rem;
        height: auto;
        z-index: 0;
        pointer-events: none;
      }

      /* ── Instruction ── */
      .dice-instruction {
        text-align: center;
        padding: 12px 16px 8px;
        font-size: 12px;
        font-weight: 600;
        letter-spacing: 1.5px;
        color: rgba(255,255,255,0.2);
        text-transform: uppercase;
        flex-shrink: 0;
        transition: all 0.2s;
      }
      .dice-instruction.dragging {
        color: var(--cyan);
        text-shadow: 0 0 10px rgba(0,245,255,0.3);
      }

      /* ── Grid ── */
      .dice-grid {
        display: grid;
        grid-template-columns: repeat(4, 1fr);
        grid-auto-rows: min-content;
        gap: 0;
        padding: 0 0.5em;
        filter: drop-shadow(2px 4px 6px black);
      }

      .dice-thumb {
        cursor: grab;
        position: relative;
        background: transparent;
        -webkit-tap-highlight-color: transparent;
        transition: opacity 0.15s;
      }
      .dice-thumb:active { cursor: grabbing; }
      .dice-thumb.dragging-source { opacity: 0.3; }

      .dice-thumb-img {
        width: 100%;
      }
      .dice-thumb-img img {
        width: 100%;
        height: auto;
        display: block;
        pointer-events: none;
      }

      .dice-equipped-badge {
        display: none;
      }

      /* ── Drag Ghost ── */
      .dice-drag-ghost {
        position: fixed;
        z-index: 9999;
        pointer-events: none;
        width: 100px;
        height: 100px;
        transform: translate(-50%, -50%) scale(1.15);
        filter: drop-shadow(0 8px 16px rgba(0,0,0,0.7)) drop-shadow(0 0 12px rgba(0,245,255,0.25));
        display: none;
      }
      .dice-drag-ghost.visible { display: block; }
      .dice-drag-ghost-img {
        width: 100%;
        height: 100%;
        object-fit: contain;
      }

      /* ── TRY Button ── */
      .dice-try-btn {
        position: absolute;
        bottom: 24px; right: 20px;
        width: 56px; height: 56px;
        border-radius: 50%;
        background: linear-gradient(135deg, var(--cyan), #0088aa);
        border: none;
        color: var(--dark-bg);
        font-family: var(--font-display);
        font-size: 14px;
        letter-spacing: 1px;
        cursor: pointer;
        box-shadow: 0 4px 20px rgba(0,245,255,0.3);
        transition: all 0.2s;
        z-index: 10;
        display: flex;
        align-items: center;
        justify-content: center;
        -webkit-tap-highlight-color: transparent;
      }
      .dice-try-btn:active { transform: scale(0.9); }
    `;
    document.head.appendChild(styles);
  }
}
